import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronDown, Leaf, Sprout, Sunrise, TreeDeciduous, Waves } from "lucide-react";
import AppShell from "@/components/AppShell";
import { GoldDots, Logo } from "@/components/Brand";
import { COACH } from "@/components/AkoCoach";
import { useApi } from "@/lib/api";

const IMG = Object.fromEntries(["moments", "words", "pause-arrive", "remember-strength", "find-what-matters", "next-step",
  "find-stillness", "return-roots", "remember-worth", "way-forward"].map((k) => [k, `/ako/${k}.webp`]));

const Thumb = ({ id, className }) => IMG[id]
  ? <img src={IMG[id]} alt="" loading="lazy" className={`object-cover ${className}`} />
  : <div className={`flex items-center justify-center bg-sage/25 text-charcoal/50 ${className}`}><Leaf size={26} strokeWidth={1.4} /></div>;

const COACH_ICONS = { vision: Sunrise, values: Leaf, methodology: Sprout, kete: TreeDeciduous, waharoa: Waves };

const CoachIcon = ({ id }) => {
  const Icon = COACH_ICONS[id] || Leaf;
  return <span className="h-14 w-14 shrink-0 rounded-xl bg-sage/20 flex items-center justify-center text-charcoal/75" data-testid={`ako-${id}-icon`}><Icon size={24} strokeWidth={1.5} /></span>;
};

const Item = ({ id, title, summary, children, thumb = true }) => {
  const [open, setOpen] = useState(false);
  return (
    <section className="ahu-card overflow-hidden fade-in" data-testid={`ako-${id}`}>
      <button className="w-full flex items-center gap-4 p-3 sm:p-4 text-left" onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`ako-${id}-toggle`}>
        {thumb ? <Thumb id={id} className="h-20 w-20 sm:h-24 sm:w-24 shrink-0 rounded-xl" />
          : <CoachIcon id={id} />}
        <span className="min-w-0 flex-1">
          <span className="block font-display text-lg sm:text-xl text-charcoal">{title}</span>
          <span className="block mt-1 text-sm text-ink-soft" data-testid={`ako-${id}-summary`}>{summary}</span>
        </span>
        <ChevronDown size={20} className="shrink-0 mr-1 text-charcoal" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .4s ease" }} />
      </button>
      {open && <div className="px-6 sm:px-8 pt-3 pb-8 fade-in">{children}</div>}
    </section>
  );
};

const kinds = (items) => [...new Set(items.map((i) => i.kind))].join(" · ");

const MembersLine = () => (
  <span className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-testid="members-only-note">
    <span className="rounded-full bg-sage/20 px-3 py-1 text-charcoal">For members</span>
    <Link to="/membership" className="underline underline-offset-4 text-charcoal" onClick={(e) => e.stopPropagation()} data-testid="members-note-link">See membership</Link>
  </span>
);

const SectionItems = ({ sec }) => sec.items.map((it) => {
  if (sec.id === "coach") {
    const c = COACH[it.id];
    return c ? <Item key={it.id} id={it.id} title={it.title} summary={c.summary} thumb={false}><c.Body /></Item> : null;
  }
  if (it.locked || !it.content) return null;
  const c = it.content;
  return sec.id === "moments" ? (
    <Item key={it.id} id={it.id} title={it.title} summary={c.intro}>
      <ul className="space-y-4">{c.questions.map((q) => <li key={q} className="font-display text-lg text-charcoal leading-snug">{q}</li>)}</ul>
    </Item>
  ) : (
    <Item key={it.id} id={it.id} title={it.title} summary={kinds(c.items)}>
      <div className="space-y-7">
        {c.items.map((w, i) => (
          <div key={i} className="border-l-2 border-sage pl-4">
            <p className="text-xs uppercase tracking-[0.12em] text-ink-soft mb-2">{w.kind}{w.title ? ` · ${w.title}` : ""}</p>
            {w.reo && <p className="font-display text-lg text-charcoal whitespace-pre-line leading-relaxed">{w.reo}</p>}
            <p className={`${w.reo ? "mt-2 text-ink-soft" : "font-display text-lg text-charcoal"} whitespace-pre-line leading-relaxed`}>{w.english}</p>
            {w.by && <p className="mt-1 text-sm text-ink-soft">{w.by}</p>}
          </div>
        ))}
      </div>
    </Item>
  );
});

const CollectionCard = ({ c, onOpen }) => (
  <div role="button" tabIndex={0} onClick={c.locked ? undefined : onOpen} onKeyDown={(e) => e.key === "Enter" && !c.locked && onOpen()}
    className={`group ahu-card overflow-hidden text-left w-full fade-in ${c.locked ? "cursor-default" : "cursor-pointer"}`} data-testid={`ako-collection-${c.id}`}>
    <div className="h-44 sm:h-56 overflow-hidden">
      {IMG[c.id] ? <img src={IMG[c.id]} alt="" className={`h-full w-full object-cover ${c.locked ? "opacity-80" : "group-hover:scale-[1.02]"}`} style={{ transition: "transform 1.2s ease" }} />
        : <div className="h-full w-full bg-charcoal flex items-center justify-center"><Logo variant="gold" className="h-12" /></div>}
    </div>
    <div className="p-6">
      <p className="font-display text-2xl text-charcoal">{c.title}</p>
      <p className="mt-1.5 text-[15px] text-ink-soft">{c.line}</p>
      {c.locked && <MembersLine />}
    </div>
  </div>
);

export default function Explore() {
  const api = useApi();
  const [open, setOpen] = useState(null);
  const [sections, setSections] = useState(null);
  useEffect(() => { api.get("/ako").then((r) => setSections(r.data)).catch(() => setSections([])); }, [api]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [open]);
  const current = sections?.find((c) => c.id === open);

  return (
    <AppShell>
      {current ? (
        <div key={open} className="fade-in" data-testid={`ako-open-${open}`}>
          <button className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-charcoal mb-6" onClick={() => setOpen(null)} data-testid="ako-back-btn">
            <ArrowLeft size={16} /> Ako
          </button>
          <h1 className="text-3xl sm:text-4xl">{current.title}</h1>
          <p className="mt-2 mb-8 text-ink-soft">{current.line} Take your time. Open one when you're ready.</p>
          <div className="space-y-4" data-testid={`explore-${open}`}><SectionItems sec={current} /></div>
        </div>
      ) : (
        <div className="fade-in">
          <p className="eyebrow mb-3">Ako</p>
          <h1 className="text-3xl sm:text-4xl">A quiet place to learn</h1>
          <p className="mt-3 mb-10 text-ink-soft max-w-xl" data-testid="ako-intro">Settle in, take a breath, and choose what you need today.</p>
          {!sections ? <div className="py-12 flex justify-center"><GoldDots /></div>
            : <div className="grid gap-5 sm:grid-cols-2">{sections.map((c) => <CollectionCard key={c.id} c={c} onOpen={() => setOpen(c.id)} />)}</div>}
        </div>
      )}
    </AppShell>
  );
}
